import { createSlice } from '@reduxjs/toolkit';
import { logout } from '../auth/auth-middleware';
import * as billingApi from 'api/rest/myaliro_billing';
import {
    fetchSubscription,
    fetchInvoices,
    fetchOutstandingInvoices,
    fetchBillingSettings,
    fetchCurrentUsage,
    fetchUsageHistory,
    fetchDisputes,
    fetchBillingData,
    updateBillingSettings,
    createDispute,
    createSubscription,
    payAndCreateSubscription,
    changePackage,
    cancelSubscription,
} from './myaliro-billing.middleware';

interface MyAliroBillingState {
    subscription: billingApi.Subscription | null;
    invoices: billingApi.Invoice[];
    outstandingInvoices: billingApi.Invoice[];
    currentInvoice: billingApi.Invoice | null;
    // paymentMethods removed - managed in sessionStorage
    settings: billingApi.BillingSettings | null;
    currentUsage: billingApi.UsageSummary | null;
    usageHistory: billingApi.UsageHistory[];
    disputes: billingApi.Dispute[];
    isLoading: boolean;
    isInvoicesLoading: boolean;
    // isPaymentMethodsLoading removed
    isSettingsLoading: boolean;
    isUsageLoading: boolean;
    error: string | null;
    successMessage: string | null;
}

const initialState: MyAliroBillingState = {
    subscription: null,
    invoices: [],
    outstandingInvoices: [],
    currentInvoice: null,
    // paymentMethods removed - managed in sessionStorage
    settings: null,
    currentUsage: null,
    usageHistory: [],
    disputes: [],
    isLoading: false,
    isInvoicesLoading: false,
    // isPaymentMethodsLoading removed
    isSettingsLoading: false,
    isUsageLoading: false,
    error: null,
    successMessage: null,
};

export const myaliroBillingSlice = createSlice({
    name: 'myaliroBilling',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ==================== Fetch Billing Data (Combined) ====================
            .addCase(fetchBillingData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchBillingData.fulfilled, (state, action) => {
                state.isLoading = false;
                const data = action.payload;

                // Handle subscription (could be null or an object with subscription: null)
                if ('subscription' in data.subscription) {
                    state.subscription = data.subscription.subscription;
                } else {
                    state.subscription = data.subscription as billingApi.Subscription;
                }

                state.outstandingInvoices = data.outstandingInvoices;
                // paymentMethods removed - managed in sessionStorage
                state.settings = data.settings;
                state.currentUsage = data.currentUsage;
                state.usageHistory = data.usageHistory || [];
            })
            .addCase(fetchBillingData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to load billing data';
            })

            // ==================== Subscription ====================
            .addCase(fetchSubscription.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSubscription.fulfilled, (state, action) => {
                state.isLoading = false;
                // Handle both cases: { subscription: Subscription } or { subscription: null }
                if ('subscription' in action.payload) {
                    state.subscription = action.payload.subscription;
                } else {
                    state.subscription = action.payload as billingApi.Subscription;
                }
            })
            .addCase(fetchSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to load subscription';
            })

            .addCase(createSubscription.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSubscription.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(createSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to create subscription';
            })

            .addCase(payAndCreateSubscription.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(payAndCreateSubscription.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(payAndCreateSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Payment failed';
            })

            .addCase(changePackage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(changePackage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(changePackage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to change package';
            })

            .addCase(cancelSubscription.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(cancelSubscription.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(cancelSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to cancel subscription';
            })

            // ==================== Invoices ====================
            .addCase(fetchInvoices.pending, (state) => {
                state.isInvoicesLoading = true;
                state.error = null;
            })
            .addCase(fetchInvoices.fulfilled, (state, action) => {
                state.isInvoicesLoading = false;
                state.invoices = action.payload.invoices;
            })
            .addCase(fetchInvoices.rejected, (state, action) => {
                state.isInvoicesLoading = false;
                state.error = action.error.message || 'Failed to load invoices';
            })

            .addCase(fetchOutstandingInvoices.pending, (state) => {
                state.isInvoicesLoading = true;
                state.error = null;
            })
            .addCase(fetchOutstandingInvoices.fulfilled, (state, action) => {
                state.isInvoicesLoading = false;
                state.outstandingInvoices = action.payload.invoices;
            })
            .addCase(fetchOutstandingInvoices.rejected, (state, action) => {
                state.isInvoicesLoading = false;
                state.error = action.error.message || 'Failed to load outstanding invoices';
            })

            // payInvoice reducers removed - manual payment not supported in Stripe-first architecture
            // Payments are handled automatically via Stripe subscriptions

            // ==================== Payment Methods ====================
            // Payment method management removed - methods managed in sessionStorage

            // ==================== Billing Settings ====================
            .addCase(fetchBillingSettings.pending, (state) => {
                state.isSettingsLoading = true;
                state.error = null;
            })
            .addCase(fetchBillingSettings.fulfilled, (state, action) => {
                state.isSettingsLoading = false;
                state.settings = action.payload;
            })
            .addCase(fetchBillingSettings.rejected, (state, action) => {
                state.isSettingsLoading = false;
                state.error = action.error.message || 'Failed to load billing settings';
            })

            .addCase(updateBillingSettings.pending, (state) => {
                state.isSettingsLoading = true;
                state.error = null;
            })
            .addCase(updateBillingSettings.fulfilled, (state, action) => {
                state.isSettingsLoading = false;
                state.successMessage = action.payload.message;
                // Update local state
                if (state.settings) {
                    state.settings = { ...state.settings, ...action.payload.settings };
                }
            })
            .addCase(updateBillingSettings.rejected, (state, action) => {
                state.isSettingsLoading = false;
                state.error = action.error.message || 'Failed to update billing settings';
            })

            // ==================== Usage ====================
            .addCase(fetchCurrentUsage.pending, (state) => {
                state.isUsageLoading = true;
                state.error = null;
            })
            .addCase(fetchCurrentUsage.fulfilled, (state, action) => {
                state.isUsageLoading = false;
                state.currentUsage = action.payload;
            })
            .addCase(fetchCurrentUsage.rejected, (state, action) => {
                state.isUsageLoading = false;
                state.error = action.error.message || 'Failed to load current usage';
            })

            .addCase(fetchUsageHistory.pending, (state) => {
                state.isUsageLoading = true;
                state.error = null;
            })
            .addCase(fetchUsageHistory.fulfilled, (state, action) => {
                state.isUsageLoading = false;
                state.usageHistory = action.payload.history;
            })
            .addCase(fetchUsageHistory.rejected, (state, action) => {
                state.isUsageLoading = false;
                state.error = action.error.message || 'Failed to load usage history';
            })

            // ==================== Disputes ====================
            .addCase(createDispute.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createDispute.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(createDispute.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to create dispute';
            })

            .addCase(fetchDisputes.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDisputes.fulfilled, (state, action) => {
                state.isLoading = false;
                state.disputes = action.payload.disputes;
            })
            .addCase(fetchDisputes.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to load disputes';
            })

            // ==================== Logout ====================
            .addCase(logout.fulfilled, (state) => {
                return initialState;
            });
    },
});

export const { clearError, clearSuccessMessage } = myaliroBillingSlice.actions;
export default myaliroBillingSlice.reducer;
